import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  subDays,
  startOfDay,
  differenceInHours,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
} from 'date-fns';
import Tabs from '@frontend/Components/Tabs';
import FilterDropdown from '@frontend/Components/FilterDropdown';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import MattersTable from './Components/MattersTable/MattersTable';
import type { MatterRow } from './Components/MattersTable/MattersTable';
import './MattersPage.css';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import ScheduleService from '../AppSettings/services/ScheduleService';
import type { ScheduleProjectSummary, ScheduleAggregateFilters } from '@frontend/types/workflows';
import useActiveEmployeeId from '../AppSettings/hooks/useActiveEmployeeId';
import useCurrentEmployee from '../AppSettings/hooks/useCurrentEmployee';
import SchedulePage from '../Projects/Components/Schedule/SchedulePage';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import PageHeader from '@frontend/Components/Titles/PageHeader/PageHeader';
import { ARCHIVED_STATUS_FILTER_VALUE } from './utils/matterArchiveStatus';

/** Map Open Date dropdown presets to an inclusive created-date calendar range. */
function getOpenDateRange(filter: string): Pick<ScheduleAggregateFilters, 'openDateFrom' | 'openDateTo'> {
  if (filter === 'all') return {};

  const now = new Date();
  const toIso = (d: Date) => format(d, 'yyyy-MM-dd');

  switch (filter) {
    case 'today': {
      const day = toIso(startOfDay(now));
      return { openDateFrom: day, openDateTo: day };
    }
    case 'week':
      return {
        openDateFrom: toIso(startOfWeek(now)),
        openDateTo: toIso(endOfWeek(now)),
      };
    case 'month':
      return {
        openDateFrom: toIso(startOfMonth(now)),
        openDateTo: toIso(endOfMonth(now)),
      };
    case 'year':
      return {
        openDateFrom: toIso(startOfYear(now)),
        openDateTo: toIso(endOfYear(now)),
      };
    default:
      return {};
  }
}

function mapAggregateToMatterRow(project: ScheduleProjectSummary): MatterRow {
  return {
    matterid: project.projectId,
    mattername: project.projectName,
    clientname: project.clientName ?? '',
    employeename: project.ownerName ?? '',
    status: project.projectStatus ?? '',
    mattertype: project.projectType ?? '',
    country: project.country ?? '',
    opendate: project.firstPlannedDate ?? undefined,
    closedate: project.lastPlannedDate ?? undefined,
    // A converted project has no application_no; its matterid IS the bid number (conversion
    // derives one from the other), so the Job Number column shows the bid number rather than N/A.
    // The raw bid UUID is deliberately never displayed.
    application_no: project.applicationNo ?? (project.bidId ? project.projectId : undefined),
    progress: project.progress ?? 0,
    milestoneCount: project.milestoneCount ?? 0,
    completedCount: project.completedCount ?? 0,
    amount_spent: undefined,
    budget: undefined,
    amount: undefined,
  };
}

/**
 * Engineering "Projects" list. This is the engineering branch of MattersPage — projects and
 * matters are the same `matters` row, so the list lives in the Matters folder and reuses
 * MattersTable. It differs from the legal list in that every filter is resolved server-side via
 * the schedule aggregation endpoint, and it adds the admin-only Schedule View tab.
 */
const ProjectsListView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const employeeId = useActiveEmployeeId();
  const currentEmployee = useCurrentEmployee();
  const permishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  const isAdmin = permishIdNum === 1 || permishIdNum === 2;

  // Read URL params for workload filter and view
  const workloadFilterFromUrl = searchParams.get('workloadFilter');
  const viewFromUrl = searchParams.get('view');

  // State management for projects list and pagination
  const [activeTab, setActiveTab] = useState<'my' | 'team' | 'schedule'>('my');
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(1);
  const [pageSize, setPageSize] = useState(7); // Items per page
  // Initialize showMyProjects based on URL param, default to true
  const [showMyProjects, setShowMyProjects] = useState(viewFromUrl !== 'team');
  const [workloadFilter, setWorkloadFilter] = useState(workloadFilterFromUrl || null); // Workload filter from URL

  // Filter dropdown states
  const [areaFilter, setAreaFilter] = useState('all');
  const [statusDropdownFilter, setStatusDropdownFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [leaderFilter, setLeaderFilter] = useState('all');
  const [openDateFilter, setOpenDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced search term that actually drives the server query, so we don't
  // fire a request on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Scope filter shared by the table, facet, and archived-count queries.
  const scopeFilters = useMemo<ScheduleAggregateFilters>(
    () => (showMyProjects && employeeId ? { userIds: [employeeId] } : {}),
    [showMyProjects, employeeId],
  );

  // Initialize view from URL params on mount
  useEffect(() => {
    if (viewFromUrl === 'team' && isAdmin) {
      setShowMyProjects(false);
    }
    if (workloadFilterFromUrl) {
      setWorkloadFilter(workloadFilterFromUrl);
    }
  }, [viewFromUrl, workloadFilterFromUrl, isAdmin]);

  // Redirect non-admin users away from restricted tabs
  useEffect(() => {
    if (!isAdmin && (activeTab === 'team' || activeTab === 'schedule')) {
      setActiveTab('my');
      setShowMyProjects(true);
    }
  }, [isAdmin, activeTab]);

  // Main table query. Search and every dropdown filter are pushed server-side so
  // they apply across the whole dataset (not just the current page), and the
  // server keeps handling pagination.
  const openDateRange = useMemo(
    () => getOpenDateRange(openDateFilter),
    [openDateFilter],
  );

  const { data, isPending, error, refetch } = useQuery<{
    items: MatterRow[];
    totalItems: number;
  }>({
    queryKey: [
      'Projects', showMyProjects, employeeId, page, pageSize,
      debouncedSearch, statusDropdownFilter, clientFilter, leaderFilter, areaFilter,
      openDateFilter,
    ],
    enabled: !showMyProjects || Boolean(employeeId),
    queryFn: async () => {
      const filters: ScheduleAggregateFilters = { ...scopeFilters };

      if (debouncedSearch) filters.search = debouncedSearch;
      if (areaFilter !== 'all') filters.country = areaFilter;
      if (clientFilter !== 'all') filters.clientName = clientFilter;
      if (leaderFilter !== 'all') filters.ownerName = leaderFilter;
      if (openDateRange.openDateFrom) filters.openDateFrom = openDateRange.openDateFrom;
      if (openDateRange.openDateTo) filters.openDateTo = openDateRange.openDateTo;

      if (statusDropdownFilter === ARCHIVED_STATUS_FILTER_VALUE) {
        filters.archived = 'only';
      } else if (statusDropdownFilter === 'all') {
        filters.archived = 'exclude';
      } else {
        filters.projectStatus = statusDropdownFilter;
      }

      const response = await ScheduleService.getAggregates({
        filters,
        pagination: { page, limit: pageSize, sortBy: 'projectName', sortOrder: 'asc' },
      });

      const aggregates = response?.data?.aggregates ?? [];
      const serverTotal = response?.data?.pagination?.total ?? aggregates.length;

      return {
        items: aggregates.map(mapAggregateToMatterRow),
        totalItems: serverTotal,
      };
    },
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  // Facet query: sample used to populate the dropdown options. It mirrors the
  // default (non-archived) view of the list so the dropdowns never offer an
  // option (area/status/client/leader) that would resolve to "No projects
  // found" simply because it only exists on archived projects. Selecting the
  // "Archived" status still surfaces those projects via the main query.
  const { data: facetItems } = useQuery<ScheduleProjectSummary[]>({
    queryKey: ['ProjectFacets', showMyProjects, employeeId],
    enabled: !showMyProjects || Boolean(employeeId),
    queryFn: async () => {
      const response = await ScheduleService.getAggregates({
        filters: { ...scopeFilters, archived: 'exclude' },
        pagination: { page: 1, limit: 100, sortBy: 'projectName', sortOrder: 'asc' },
      });
      return response?.data?.aggregates ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Accurate archived count across the whole scope (for the dropdown badge).
  const { data: archivedCount = 0 } = useQuery<number>({
    queryKey: ['ProjectArchivedCount', showMyProjects, employeeId],
    enabled: !showMyProjects || Boolean(employeeId),
    queryFn: async () => {
      const response = await ScheduleService.getAggregates({
        filters: { ...scopeFilters, archived: 'only' },
        pagination: { page: 1, limit: 1, sortBy: 'projectName', sortOrder: 'asc' },
      });
      return response?.data?.pagination?.total ?? 0;
    },
    staleTime: 60 * 1000,
  });

  // Build dropdown options from the facet sample.
  const filterOptions = useMemo(() => {
    const source = facetItems ?? [];
    if (!source.length) {
      return { areas: [], statuses: [], clients: [], leaders: [] };
    }

    const areas = new Set<string>();
    const statuses = new Set<string>();
    const clients = new Set<string>();
    const leaders = new Set<string>();

    // Only keep real, non-blank values so empty/whitespace-only fields never
    // become a clickable (but meaningless) dropdown option.
    const addIfPresent = (set: Set<string>, value?: string | null) => {
      const trimmed = typeof value === 'string' ? value.trim() : '';
      if (trimmed) set.add(trimmed);
    };

    source.forEach((project) => {
      addIfPresent(statuses, project.projectStatus);
      addIfPresent(clients, project.clientName);
      addIfPresent(leaders, project.ownerName);
      addIfPresent(areas, project.country);
    });

    return {
      areas: Array.from(areas).sort().map((area) => ({ value: area, label: area })),
      statuses: Array.from(statuses).sort().map((status) => ({ value: status, label: status })),
      clients: Array.from(clients).sort().map((client) => ({ value: client, label: client })),
      leaders: Array.from(leaders).sort().map((leader) => ({ value: leader, label: leader })),
    };
  }, [facetItems]);

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All Statuses' },
      {
        value: ARCHIVED_STATUS_FILTER_VALUE,
        label: (
          <span className="filter-option-archived">
            <span>Archived</span>
            <span
              className="matters-archived-count-badge matters-archived-count-badge--in-option"
              title={`${archivedCount} archived projects`}
            >
              {archivedCount}
            </span>
          </span>
        ),
      },
      ...filterOptions.statuses,
    ],
    [archivedCount, filterOptions.statuses]
  );

  // Helper function to categorize project by workload status (same logic as ActiveWorkloadWidget and MattersPage)
  const getWorkloadStatus = useCallback((project: any) => {
    const now = new Date();
    const ninetyDaysAgo = subDays(startOfDay(now), 90);
    const status = (project.status || '').trim().toLowerCase();
    const isCompleted = status === 'completed' || status === 'closed';

    // Check if project is completed within last 90 days
    if (isCompleted && project.closedate) {
      try {
        const closeDate = startOfDay(new Date(project.closedate));
        const nowDate = startOfDay(now);
        if (closeDate >= ninetyDaysAgo && closeDate <= nowDate) {
          return 'completed';
        }
      } catch (e) {
        // Invalid date, continue to check other statuses
      }
    }

    // Only categorize non-completed projects
    if (!isCompleted || !project.closedate) {
      // Check due date (closedate) for at-risk/in-progress categorization
      if (project.closedate) {
        try {
          const dueDate = new Date(project.closedate);
          const hoursUntilDue = differenceInHours(dueDate, now);

          // At Risk: due within 24 hours or past due
          if (hoursUntilDue <= 24) {
            return 'at-risk';
          }
          // In Progress: due 24+ hours away
          else {
            return 'in-progress';
          }
        } catch (e) {
          // Invalid date, count as in progress
          return 'in-progress';
        }
      } else {
        // No due date, count as in progress
        return 'in-progress';
      }
    }

    // If completed but not in last 90 days, don't include in any category
    return null;
  }, []);

  // Search, status, client, leader, area and open-date filters are all resolved
  // server-side. The only filter left to apply client-side is the date-derived
  // workload filter (used when navigating in from dashboard widgets).
  useEffect(() => {
    if (!data) return;

    let result = data.items;
    if (workloadFilter) {
      result = result.filter((p) => getWorkloadStatus(p) === workloadFilter);
    }

    setFilteredProjects(result);
    setPageLimit(Math.max(1, Math.ceil(data.totalItems / pageSize)));
  }, [data, pageSize, workloadFilter, getWorkloadStatus]);

  // Handle pagination changes
  const handlePagination = (nextPage: number) => setPage(nextPage);

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Handle table refresh
  const handleTableRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handle search functionality
  const handleSearch = useCallback((search: string, _filter?: string | null) => {
    setSearchTerm(search || '');
    setPage(1);
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId as 'my' | 'team' | 'schedule');

    if (tabId === 'schedule') {
      setShowMyProjects(false);
      setPage(1);
      return;
    }

    setShowMyProjects(tabId === 'my');
    setPage(1);
    refetch();
  }, [refetch]);

  // Loading and error states
  if (isPending) return <span>Loading...</span>;
  if (error) return <span>Error: {(error as Error).message}</span>;

  // Main render
  return (
    <div className="mattersPage">
      <PageHeader
        title="Projects"
        subtitle="Manage and track all projects"
      >
        {isAdmin && (
          <CreateButton
            onClick={() => navigate('/createProjects')}
            text="New Project"
            showIcon
            aria-label="Create new project"
          />
        )}
      </PageHeader>

      {/* White Container for Tabs, Search, and Filters */}
      <div className="matters-content-container">
        {/* Tabs and Search Section */}
        <div className="matters-tabs-search-section">
          <div className="matters-tabs-section">
            <Tabs
              tabs={[
                { id: 'my', label: 'My Projects' },
                ...(isAdmin ? [
                  { id: 'team', label: "Team's Projects" },
                  { id: 'schedule', label: 'Schedule View' },
                ] : []),
              ]}
              activeTabId={activeTab}
              onTabChange={handleTabChange}
              variant="pills"
            />
            {activeTab !== 'schedule' && (
              <span className="matters-count">
                Showing {filteredProjects.length} of {data?.totalItems || 0} projects
              </span>
            )}
          </div>
          {activeTab !== 'schedule' && (
            <div className="matters-search-section">
              <SearchBar
                onSearch={handleSearch}
                placeholder="Search projects..."
                className="matters-search-bar"
              />
            </div>
          )}
        </div>

        {/* Filter Dropdowns Row */}
        {activeTab !== 'schedule' && (
          <div className="matters-filters-row">
            <FilterDropdown
              label=""
              placeholder="All Areas"
              options={[
                { value: 'all', label: 'All Areas' },
                ...filterOptions.areas,
              ]}
              value={areaFilter}
              onChange={(value) => {
                setAreaFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              className="matters-filter-dropdown"
            />
            <FilterDropdown
              label=""
              placeholder="All Statuses"
              options={statusFilterOptions}
              value={statusDropdownFilter}
              onChange={(value) => {
                setStatusDropdownFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              className="matters-filter-dropdown"
            />
            <FilterDropdown
              label=""
              placeholder="All Clients"
              options={[
                { value: 'all', label: 'All Clients' },
                ...filterOptions.clients,
              ]}
              value={clientFilter}
              onChange={(value) => {
                setClientFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              className="matters-filter-dropdown"
            />
            <FilterDropdown
              label=""
              placeholder="All Leaders"
              options={[
                { value: 'all', label: 'All Leaders' },
                ...filterOptions.leaders,
              ]}
              value={leaderFilter}
              onChange={(value) => {
                setLeaderFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              className="matters-filter-dropdown"
            />
            <FilterDropdown
              label=""
              placeholder="Open Date"
              options={[
                { value: 'all', label: 'All Dates' },
                { value: 'today', label: 'Today' },
                { value: 'week', label: 'This Week' },
                { value: 'month', label: 'This Month' },
                { value: 'year', label: 'This Year' },
              ]}
              value={openDateFilter}
              onChange={(value) => {
                setOpenDateFilter(Array.isArray(value) ? value[0] : value);
                setPage(1);
              }}
              className="matters-filter-dropdown"
            />
          </div>
        )}
      </div>

      {/* Projects table showing current page of data or Schedule view */}
      {activeTab === 'schedule' ? (
        <div className="schedule-view-wrapper">
          <SchedulePage />
        </div>
      ) : (
        <MattersTable
          data={filteredProjects}
          showLeader={!showMyProjects}
          pageSize={pageSize}
          onMatterRefresh={handleTableRefresh}
          pagination={{
            currPage: page,
            onPageChange: handlePagination,
            pageLimit: pageLimit,
            pageSize: pageSize,
            onPageSizeChange: handlePageSizeChange,
            totalItems: data?.totalItems || 0,
            itemLabel: 'projects',
          }}
        />
      )}
    </div>
  );
};

export default ProjectsListView;
